import WashdayClientSingleton from './WashdayClientSingleton';
import { IProductInvoiceModuleSettings } from '../interfaces/Product';

type ProductInvoiceModulePayload = {
    invoice_description?: string;
    invoice_product_key?: string;
    invoice_product_unit_key?: string;
    invoice_product_unit_name?: string;
    invoiceModuleSettings?: IProductInvoiceModuleSettings;
};

type CfdiPayloadField =
    | 'invoice_description'
    | 'invoice_product_key'
    | 'invoice_product_unit_key'
    | 'invoice_product_unit_name';

type CfdiSettingsField = 'description' | 'productKey' | 'unitKey' | 'unitName';

const withProductInvoiceModuleSettings = <T extends ProductInvoiceModulePayload>(data: T): T => {
    const existingSettings = data.invoiceModuleSettings || {};
    const existingCfdi = existingSettings.cfdi || {};
    const cfdi: NonNullable<IProductInvoiceModuleSettings['cfdi']> = {
        ...existingCfdi,
    };

    const setCfdiValue = (
        payloadField: CfdiPayloadField,
        settingsField: CfdiSettingsField
    ) => {
        if (Object.prototype.hasOwnProperty.call(data, payloadField)) {
            const value = data[payloadField];
            cfdi[settingsField] = value ?? '';
            return;
        }

        if (typeof existingCfdi[settingsField] !== 'undefined') {
            cfdi[settingsField] = existingCfdi[settingsField];
        }
    };

    setCfdiValue('invoice_description', 'description');
    setCfdiValue('invoice_product_key', 'productKey');
    setCfdiValue('invoice_product_unit_key', 'unitKey');
    setCfdiValue('invoice_product_unit_name', 'unitName');

    if (Object.keys(cfdi).length === 0) {
        return data;
    }

    return {
        ...data,
        invoiceModuleSettings: {
            ...existingSettings,
            cfdi: {
                ...existingCfdi,
                ...cfdi,
            },
        },
    };
};


export const getProductByIdV2 = async (id: string, storeId: string) => {
    try {
        const productsApi = WashdayClientSingleton.getInstance().products;
        const [productResponse, suppliesResponse] = await Promise.all([
            productsApi.getById(id),
            productsApi.getProductSupplies(storeId, id),
        ]);

        return {
            ...productResponse,
            data: {
                ...productResponse.data,
                data: {
                    ...productResponse.data?.data,
                    productSupplies: suppliesResponse.data?.data?.supplies ?? [],
                },
            },
        };
    } catch (e) {
        console.log('ERROR getProductByIdV2', e);
        throw e;
    }
};

export const createProductV2 = async (data: {
    name: string;
    description: string;
    price: number;
    expressPrice: number;
    type: 'normal' | 'weight' | 'length';
    minimumBillableQuantity?: number | null;
    pieces: number;
    sku?: string;
    overlayText: string;
    order: number;
    taxExemptOne: boolean;
    taxExemptTwo: boolean;
    taxExemptThree: boolean;
    showInApp: boolean;
    image: string | null;
    store: string | null;
    section: string;
    subcategory?: string | null;
    invoice_description: string;
    invoice_product_key: string;
    invoice_product_unit_key: string;
    invoice_product_unit_name: string;
    invoiceModuleSettings?: IProductInvoiceModuleSettings;
    productSupplies: Array<any>,
    isOutsourced?: boolean,
    outsourcingPartner?: string
}) => {
    try {
        return await WashdayClientSingleton.getInstance().products.create(
            withProductInvoiceModuleSettings(data)
        );
    } catch (e) {
        console.log('ERROR createProductV2', e);
        throw e;
    }
};

export const deleteProductByIdV2 = async (id: string) => {
    try {
        return await WashdayClientSingleton.getInstance().products.deleteById(id);
    } catch (e) {
        console.log('ERROR deleteProductByIdV2', e);
        throw e;
    }
};

export const updateProductByIdV2 = async (id: string, data: {
    name?: string;
    description?: string;
    price?: number;
    expressPrice?: number;
    type?: 'normal' | 'weight' | 'length';
    minimumBillableQuantity?: number | null;
    pieces?: number;
    sku?: string;
    overlayText?: string;
    order?: number;
    taxExemptOne?: boolean;
    taxExemptTwo?: boolean;
    taxExemptThree?: boolean;
    showInApp?: boolean;
    image?: string;
    invoice_product_key?: string,
    invoice_description?: string,
    invoice_product_unit_key?: string
    invoice_product_unit_name?: string,
    invoiceModuleSettings?: IProductInvoiceModuleSettings,
    isOutsourced?: boolean,
    outsourcingPartner?: string,
    subcategory?: string | null,
    store?: string | null
}) => {
    try {
        return await WashdayClientSingleton.getInstance().products.updateById(
            id,
            withProductInvoiceModuleSettings(data)
        );
    } catch (e) {
        console.log('ERROR updateSectionByIdV2', e);
        throw e;
    }
};

export const createProductSupplyV2 = async (storeId: string, id: string, data: {
    supplyId: string;
    usageAmount: number;
}) => {
    try {
        return await WashdayClientSingleton.getInstance().products.createProductSupply(storeId, id, data);
    } catch (e) {
        console.log('ERROR createProductSupplyV2', e);
        throw e;
    }
};

export const deleteProductSupplyByIdV2 = async (storeId: string, id: string, productSupplyId: string) => {
    try {
        return await WashdayClientSingleton.getInstance().products.deleteProductSupplyById(storeId, id, productSupplyId);
    } catch (e) {
        console.log('ERROR deleteProductSupplyByIdV2', e);
        throw e;
    }
};

export const exportProductList = async (storeId: string, options?: { empty?: boolean }) => {
    try {
        return await WashdayClientSingleton.getInstance().csv.exportProductsList(storeId, options);
    } catch (e) {
        console.log('ERROR exportProductsList', e);
        throw e;
    }
};

/** @deprecated Legacy CSV product bulk-create flow. Use the XLSX Product Bulk import. */
export const productsBulkCreate = async (storeID: string, sectionID: string, data: FormData) => {
    try {
        const res = await WashdayClientSingleton.getInstance().products.bulkCreate(storeID, data);
        return { ...res.data };
    } catch (e) {
        console.log('ERROR productsBulkCreate', e);
        throw e;
    }
};

// Price Options
export const createProductPriceOptionV2 = async (productId: string, data: {
    label: string;
    description?: string;
    price: number;
    expressPrice: number;
    isDefault?: boolean;
    isActive?: boolean;
}) => {
    try {
        return await WashdayClientSingleton.getInstance().products.createPriceOption(productId, data);
    } catch (e) {
        console.log('ERROR createProductPriceOptionV2', e);
        throw e;
    }
};

export const updateProductPriceOptionV2 = async (productId: string, optionId: string, data: {
    label?: string;
    description?: string;
    price?: number;
    expressPrice?: number;
    isDefault?: boolean;
    isActive?: boolean;
}) => {
    try {
        return await WashdayClientSingleton.getInstance().products.updatePriceOption(productId, optionId, data);
    } catch (e) {
        console.log('ERROR updateProductPriceOptionV2', e);
        throw e;
    }
};

export const deleteProductPriceOptionV2 = async (productId: string, optionId: string) => {
    try {
        return await WashdayClientSingleton.getInstance().products.deletePriceOption(productId, optionId);
    } catch (e) {
        console.log('ERROR deleteProductPriceOptionV2', e);
        throw e;
    }
};
